import intersection from "lodash/intersection"
import union from "lodash/union"
import { FiltersState } from "../states/filters"
import { USAGE_TO_CONTENT_TYPES } from "../constants/filters"
import { Role } from "../../mp-database/types/Role"
import { Roles } from "../../mp-utils/generic-utils"
import { AssignmentStatus } from "../../../types/gqlTypes/graphql"
import { Column } from "../components/Table"
import { FeatureFlagStatesType } from "../../mp-utils/generic-utils/feature-flags/types"
import { getHiddenFieldsByFeatureFlag } from "../providers/ReportingProvider/helpers"

export const getItemsByFiltersAndView = (
  page: string,
  itemsByView: string[],
  itemsByFilter: Record<string, Record<string, string[]>>,
  filters: FiltersState,
  type?: "content-widget" | null
) => {
  let filterItems = []

  switch (type) {
    case "content-widget": {
      if (["content"].includes(page)) {
        filterItems = Object.entries(filters || {}).reduce(
          (res, [filter, values]) => {
            if (!values?.length) return res

            const updatedFilter = filter === "usageContentType" && "contentType"
            const updatedValues =
              filter === "usageContentType"
                ? Array.from(
                    new Set(values.map(item => USAGE_TO_CONTENT_TYPES[item]))
                  )
                : values

            const items = updatedValues
              .map(
                value => itemsByFilter[updatedFilter || filter]?.[value] ?? null
              )
              .filter(filters => filters)
              .flat()

            return Array.from(new Set([...res, ...items]))
          },
          []
        )
      }

      break
    }

    default: {
      filterItems = union(
        Object.entries(filters)
          .map(([filter, values]) => {
            if (values?.length > 0) {
              // For each value of the filter, find intersection of applicable actions.
              return intersection(
                ...values
                  .map(value => itemsByFilter[filter]?.[value] ?? null)
                  .filter(filters => filters)
              )
            }

            return null
          })
          .filter(filters => filters)
          .flat()
      )
    }
  }

  if (filterItems.length > 0) {
    return filterItems
  }

  return itemsByView
}

export const downloadLinkByForm = (apiUrl: string, token: string) => {
  const form = document.createElement("form")
  form.method = "GET"
  form.action = apiUrl

  const tokenInput = document.createElement("input")
  tokenInput.type = "hidden"
  tokenInput.name = "token"
  tokenInput.value = token
  form.appendChild(tokenInput)

  document.body.appendChild(form)
  form.submit()
  document.body.removeChild(form)
}

export const getFilteredActions = (args: {
  page: string
  subpage: string
  userRole: Role
  record: any
  actions: any[]
}) => {
  const { page, subpage, userRole, record, actions } = args

  if (page === "organization" && subpage === "admins") {
    const targetRole = record?.["org_view_admins.role"]

    // Only Super Administrator can manage Super Administrators
    if (
      targetRole === "Super Administrator" &&
      userRole !== "Super Administrator"
    ) {
      return actions.filter(
        action => !["delete", "edit", "view"].includes(action.id)
      )
    }

    // ACT Admin can manage Organization Admin, District Admin, and School Admin
    if (userRole === "ACT Admin") {
      if (
        !["Organization Admin", "District Admin", "School Admin"].includes(
          targetRole
        )
      ) {
        return actions.filter(action => !["delete", "edit"].includes(action.id))
      }
    }

    // Organization Admin can manage District Admin and School Admin
    if (userRole === "Organization Admin") {
      if (!["District Admin", "School Admin"].includes(targetRole)) {
        return actions.filter(action => !["delete", "edit"].includes(action.id))
      }
    }

    // District Admin can manage District Admin and School Admin
    if (userRole === "District Admin") {
      if (!["District Admin", "School Admin"].includes(targetRole)) {
        return actions.filter(action => !["delete", "edit"].includes(action.id))
      }
    }

    // School Admin can only manage School Admin
    if (userRole === "School Admin") {
      if (targetRole !== "School Admin") {
        return actions.filter(action => !["delete", "edit"].includes(action.id))
      }
    }
  }

  // ACT roles cannot edit or delete districts
  if (page === "organization" && subpage === "districts") {
    if (userRole === "ACT Admin" || userRole === "Organization Admin") {
      return actions.filter(action => !["delete", "edit"].includes(action.id))
    }
  }

  // Teachers can not delete students
  if (page === "organization" && subpage === "students") {
    if (userRole === Roles.Teacher) {
      return actions.filter(action => !["delete"].includes(action.id))
    }
  }

  // License assignment actions based on license status
  if (page === "organization" && subpage === "license-assign") {
    const licenseStatus = record?.["org_view_students.hasActiveLicense"]

    return actions.filter(action => {
      // Show assign action only for unlicensed students
      if (action.id === "assignLicense") {
        return licenseStatus !== "Licensed"
      }
      // Show unassign action only for licensed students
      if (action.id === "unassignLicense") {
        return licenseStatus === "Licensed"
      }
      // Allow all other actions
      return true
    })
  }

  // CUSTOM REPORTS: Disable row actions based on status of report
  if (page === "insights" && subpage === "custom-reports") {
    const status = record?.status

    if (status === "Queued" || status === "Generating") {
      return actions.map(action => ({ ...action, disabled: true }))
    }

    if (status === "Failed") {
      const unavailable = new Set(["view", "download", "duplicate"])
      return actions.map(action => ({
        ...action,
        disabled: unavailable.has(action.id as string)
      }))
    }
  }

  // Hide "End Assignment" action for closed assignments
  if (page === "teaching-tools" && subpage === "assignments") {
    const assignmentStatus: AssignmentStatus =
      record?.["assignments.assignmentStatus"]

    return actions.filter(action => {
      // Hide endAssignment if assignment is already closed
      if (action.id === "endAssignment") {
        return assignmentStatus !== "CLOSED"
      }
      // Allow all other actions
      return true
    })
  }

  return actions
}

// Removes columns whose name is in excludedFields (including nested group columns)
export const filterColumnsByExcluded = (
  columns: Column[],
  excludedFields: string[]
): Column[] => {
  if (!excludedFields.length) return columns

  return columns
    .filter(col => !excludedFields.includes(col.name))
    .map(col => {
      if (col.columns?.length) {
        const filtered = filterColumnsByExcluded(col.columns, excludedFields)
        return filtered.length ? { ...col, columns: filtered } : null
      }
      return col
    })
    .filter(col => col !== null)
}

export const getFilteredColumnsByFeatureFlags = (
  columns: Column[],
  featureFlags: FeatureFlagStatesType
) => {
  const excludedFields = getHiddenFieldsByFeatureFlag(featureFlags)

  return filterColumnsByExcluded(columns, excludedFields)
}
